import { Resource } from '../models/Resource'
import { TopicApi } from '../services/api-objects/TopicApi'
import { getNodeId, Instance } from 'mobx-state-tree'
import { Question } from '../models/Question'
import { Topic } from '../models/Topic'
import { QuizHelper } from './QuizHelper'
import { ResourceHelper } from './ResourceHelper'
import { QuestionHelper } from './QuestionHelper'
import { shuffle, groupBy, sortBy } from 'lodash'
import { Quiz } from '../models/Quiz'

let rootStore

export class TopicHelper {
  static setRootStore = (store: any) => {
    rootStore = store
  }

  static importTopics = (data, allowEmpty?) => {
    let untitledCount = 0
    data.map((row) => {
      if (!row.topic) {
        untitledCount++
        row.topic = `Untitled Topic ${untitledCount}`
      }
    })

    // no topics found
    if (untitledCount === data?.length && !allowEmpty) {
      return []
    }

    const topics = groupBy(data, 'topic')
    return Object.keys(topics).map((title) => {
      return this.createNewTopic({
        title: title.includes('Untitled Topic') ? '' : title,
        touched: true,
        quizzes: QuizHelper.importQuizzes(topics[title], true),
      })
    })
  }

  static loadTopic = async (topicId: number) => {
    let newTopic: Instance<typeof Topic> | null = null
    const [getTopicResponse, getTopicResourcesResponse] = await Promise.all([
      TopicApi.getTopic(topicId),
      TopicApi.getResources(
        false,
        [
          { field: 'source_created_date' },
          { field: 'source_title' },
          { field: 'source_filename_url' },
          { field: 'source_file_size' },
          { field: 'source_file_type' },
          { field: 'source_mimetype' },
          { field: 'topic_source_required' },
        ],
        'list_topic_sources',
        topicId,
      ),
    ])

    const topic = getTopicResponse.data?.topic?.pop()
    if (getTopicResponse.ok && topic) {
      newTopic = Topic.create({
        ...topic,
        selected: true,
        original_name: topic.title,
        imageUrl: topic.image,
        keep_item_order: topic.keep_item_order,
        quizzes: (topic.quizzes ?? ([] as Array<Instance<typeof Quiz>>))
          .sort((a, b) => Number(a?.order) - Number(b?.order))
          .map((q) => {
            const cards =
              q.cards?.map((card) => {
                const cardQuestion = QuestionHelper.createNewQuestion('Card', {
                  card_json: card.versions[0].card_json,
                  card_name: card.card_name,
                  original_name: card.card_name,
                  card_id: card.card_id,
                  order: card.order,
                  approved: true,
                })
                return cardQuestion
              }) || []
            const questions = [
              ...cards,
              ...(q.questions ?? ([] as Array<Instance<typeof Question>>)).map(
                (question, index) => {
                  return {
                    ...question,
                    advanced: Boolean(question.choices.some((choice) => choice.factoid)),
                    approved: true,
                    touched: true,
                    id: question.question_id,
                    is_locked: false,
                    image_src: question.image,
                  }
                },
              ),
            ]
            return {
              ...q,
              order: q.order,
              keep_item_order: q.keep_item_order,
              is_locked: false,
              original_name: q.title,
              cards: [],
              questions: q?.keep_item_order ? sortBy(questions, ['order']) : shuffle(questions),
            }
          }),
      })
    } else {
      throw new Error(`failed to get topic ${topicId}`)
    }

    if (getTopicResourcesResponse.ok && getTopicResourcesResponse.data?.rows.length) {
      newTopic.set(
        'resources',
        ResourceHelper.getResourceReadToResource(getTopicResourcesResponse.data.rows),
      )
    }

    if (newTopic.quizzes?.length) {
      newTopic.quizzes[0].select()
      if (newTopic.quizzes[0].questions) {
        newTopic.quizzes[0].questions[0].select()
      }
      newTopic.quizzes.forEach((q) =>
        QuizHelper.loadResources(q).then(() => {
          const unattachedResources = [] as any
          q?.questions?.map((question) => {
            if (question.resource_id) {
              const found = q?.resources?.find((r) => r.resource_id === question.resource_id)
              if (!found) {
                unattachedResources.push(question.resource_id)
              }
            }
          })
          q?.set('removingResources', unattachedResources)
        }),
      )
    }

    return newTopic
  }

  static loadResources = async () => {}

  static createNewTopic = (init?: any) => {
    return Topic.create({
      title: '',
      selected: true,
      keep_item_order: false,
      quizzes: [],
      resources: [],
      ...init,
    })
  }

  static hasResource = (
    resourceToFind: Instance<typeof Resource>,
    topic: Instance<typeof Topic>,
  ) => {
    if (!topic.resources?.length) {
      return false
    }

    // check if in the resources array already or if its going to be added
    return (
      !!topic.resources.find((r) => r.source_id === resourceToFind.source_id) ||
      !!(
        topic.addingResources &&
        topic?.addingResources.find((r) => r.source_id === resourceToFind.source_id)
      )
    )
  }

  static publishImages = async (topic: Instance<typeof Topic>) => {
    if (!topic.topic_id) {
      return
    }

    if (topic.imageFile) {
      await TopicApi.publishImage(topic.topic_id, topic.imageFile)
    } else if (topic.image) {
      // do nothing
    } else if (topic.topic_id) {
      await TopicApi.removeImage(topic.topic_id)
    }

    // if (topic.quizzes?.length) {
    //   await topic.quizzes.forEach(async (q) => await QuizHelper.publishImages(q))
    // }
  }

  static publishResources = async (topic: Instance<typeof Topic>) => {
    if (!topic.topic_id) {
      return
    }

    if (topic?.removingResources && topic?.removingResources.length) {
      await TopicApi.removeResources(topic.topic_id, topic?.removingResources)
    }

    if (topic.addingResources && topic?.addingResources?.length) {
      const payload = topic.addingResources.filter((r) => {
        return topic?.quizResources?.find((qr) => qr.source_id === r.source_id) ? false : true
      })
      await TopicApi.addResources(topic.topic_id!, payload)
    }
  }

  static updateResources = async (
    topic: Instance<typeof Topic>,
    resource: Instance<typeof Resource>,
  ) => {
    if (topic.resources?.length) {
      topic.resources.forEach((r) => {
        if (r.source_id === resource.source_id) {
          r.set('title', resource.title)
        }
      })
    }

    if (topic.quizzes?.length) {
      topic.quizzes.forEach((q) => QuizHelper.updateResources(q, resource))
    }
  }

  static removeResourceFromQuizByNodeId = async (
    topic: Instance<typeof Topic>,
    nodeId: number,
    resource: Instance<typeof Resource>,
  ) => {
    if (!topic.quizzes?.length) {
      throw new Error(`Topic has no quizzes to remove resource from`)
    }

    topic.quizzes.forEach((q) => {
      if (getNodeId(q) === nodeId) {
        q.removeResource(resource)
      }
    })
  }
}
